import React, { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getAllLogsAction } from '../../redux/actions/userActions';

import Content from '../../components/Content';
import Pagination from '../../components/Pagination';

import LogStyles from './Logs.module.css';
import LogTable from './LogTable';
import { orgsListAction } from '../../redux/actions/orgActions';
import ViewLogModal from './ViewLogModal';
import Filters from './Filters';

const LogsScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [logDataLoading, setLogDataLoading] = React.useState(false);
  const [searchKey, setSearchKey] = React.useState('');
  const [logDetails, setLogDetails] = React.useState({ status: false, data: {} });

  const [totalPageSize, setTotalPageSize] = React.useState(0);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [selectedClient, setSelectedClient] = React.useState('');

  const [selectedTags, setSelectedTags] = React.useState([]);
  const [selectedRange, setSlectedRange] = React.useState('all');
  const [dateRange, setDateRange] = React.useState({
    startDate: '',
    endDate: '',
  });
  const [selectedUserId, setSelectedUserId] = React.useState('');
  const [users, setUsers] = React.useState([]);

  const pageLimit = 15;

  const userLogin = useSelector(state => state.userLogin);
  const logsList = useSelector(state => state.logsList);
  const orgsList = useSelector(state => state.orgsList);

  const formatDate = inputDate => {
    if (!inputDate) return '';
    const [year, month, day] = inputDate.split('-');
    const formattedDate = `${month}-${day}-${year}`;
    return formattedDate;
  };

  React.useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      dispatch(
        getAllLogsAction(pageNumber, selectedTags.join(',').toLowerCase(), selectedClient, searchKey, formatDate(dateRange.startDate), formatDate(dateRange.endDate), selectedUserId,pageLimit)
      );
    } else {
      dispatch(getAllLogsAction(pageNumber, selectedTags.join(',').toLowerCase(), selectedClient, searchKey, '', '', selectedUserId, pageLimit));
    }
  }, [dispatch, pageNumber, searchKey, selectedTags, selectedClient, dateRange, selectedUserId]);

  React.useEffect(() => {
    if (logsList && logsList.userInfo && logsList.userInfo.status === 'success' && logsList.userInfo.data && logsList.userInfo.data) {
      setTotalPageSize(logsList.userInfo.data.totalResults);
    }
  }, [logsList]);

  React.useEffect(() => {
    if (userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role === 'admin') {
      dispatch(orgsListAction());
    }
  }, [dispatch, userLogin]);

  // Fetch users for the filter dropdown. Deliberately depends only on
  // the logged-in user's id (stable across the session) instead of the
  // whole `userLogin` object — that object mutates on every unrelated
  // reducer touch (notification refresh, socket connect, tenant-config
  // load, etc.), and each mutation re-fired this fetch, writing a new
  // "Viewed users" audit row every time. Anjani saw 6-7 rows/minute
  // per login on ch.getregure.com from this one effect. D-30.
  const loggedInUserId =
    userLogin?.userInfo?.data?.user?._id ||
    userLogin?.userInfo?.data?.data?._id ||
    null;
  React.useEffect(() => {
    if (!loggedInUserId) return;
    const fetchUsers = async () => {
      try {
        const BASE_URL = process.env.REACT_APP_BASE_URL || '';
        const response = await fetch(`${BASE_URL}/users`, { credentials: 'include' });
        const data = await response.json();
        if (data.status === 'success' && data.data && data.data.data) {
          setUsers(data.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchUsers();
  }, [loggedInUserId]);

  const handlePageChange = async currentPage => {
    setPageNumber(currentPage.selected + 1);
  };

  const refreshLogs = () => {
    setPageNumber(1);
    setSlectedRange('all');
    setSelectedTags([]);
    setSearchKey('');
    setDateRange({ startDate: '', endDate: '' });
    dispatch(getAllLogsAction(1, [].join(',').toLowerCase(), selectedClient, pageLimit));
  };

  return (
    <Content
      dropdownFilter={
        userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role === 'admin' ? true : false
      }
      dropdownName='select-client'
      dropdownOptions={
        orgsList && orgsList.loading ? (
          <option value=''>{t('screens.logs.loading')}</option>
        ) : (
          <Fragment>
            <option value=''>{t('screens.logs.showAllUsers')}</option>
            {orgsList && orgsList.orgs && orgsList.orgs.data && orgsList.orgs.data.data && orgsList.orgs.data.data.length > 0 ? (
              orgsList.orgs.data.data.map(org => (
                <option value={org._id} key={org._id}>
                  {org.name}
                </option>
              ))
            ) : (
              <option value=''>{t('screens.logs.noOrgs')}</option>
            )}
          </Fragment>
        )
      }
      dropdownOnChange={e => {
        setSelectedClient(e.target.value);
        setLogDataLoading(true);
      }}
      currentMenu='logs'
      headerTitle={t('screens.logs.headerTitle')}
      addBtn={true}
      addBtnIcon='refresh'
      addBtnTitle={t('screens.logs.refreshLogs')}
      addBtnClick={refreshLogs}
      searchBox={false}
    >
      <Filters
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        setPageNumber={setPageNumber}
        selectedRange={selectedRange}
        setSlectedRange={setSlectedRange}
        selectedUserId={selectedUserId}
        setSelectedUserId={setSelectedUserId}
        users={users}
      />
      <div className={LogStyles.content}>
        <LogTable logData={logsList} setLogDataLoading={setLogDataLoading} logDataLoading={logDataLoading} setLogDetails={setLogDetails} />
      </div>
      <div className='table__footer'>
        <p />
        <Pagination onPageChange={handlePageChange} rowsPerPage={pageLimit} totalPageSize={totalPageSize} pageNumber={pageNumber} />
      </div>

      {logDetails.status && <ViewLogModal logDetails={logDetails} handleCloseLogModal={() => setLogDetails({ status: false, data: {} })} />}
    </Content>
  );
};

export default LogsScreen;
